/**
 * Application-wide time zone preference (IANA). Persisted in localStorage.
 * Empty string = use browser local offset (omit timeZone in Intl).
 *
 * Log timestamps from the API are UTC wall-clock values (Java LocalDateTime / ISO
 * without offset). We parse naive ISO strings as UTC, then format in the user's zone.
 */

export const TIMEZONE_STORAGE_KEY = 'commrayTimezone';

export const TIMEZONE_OPTIONS = [
  { value: '', label: 'Browser default (local)' },
  { value: 'UTC', label: 'UTC' },
  { value: 'America/New_York', label: 'America — New York' },
  { value: 'America/Chicago', label: 'America — Chicago' },
  { value: 'America/Denver', label: 'America — Denver' },
  { value: 'America/Los_Angeles', label: 'America — Los Angeles' },
  { value: 'America/Toronto', label: 'America — Toronto' },
  { value: 'America/Vancouver', label: 'America — Vancouver' },
  { value: 'America/Sao_Paulo', label: 'America — São Paulo' },
  { value: 'Europe/London', label: 'Europe — London' },
  { value: 'Europe/Paris', label: 'Europe — Paris' },
  { value: 'Europe/Berlin', label: 'Europe — Berlin' },
  { value: 'Europe/Moscow', label: 'Europe — Moscow' },
  { value: 'Asia/Dubai', label: 'Asia — Dubai' },
  { value: 'Asia/Kolkata', label: 'Asia — Kolkata' },
  { value: 'Asia/Shanghai', label: 'Asia — Shanghai' },
  { value: 'Asia/Tokyo', label: 'Asia — Tokyo' },
  { value: 'Asia/Seoul', label: 'Asia — Seoul' },
  { value: 'Australia/Sydney', label: 'Australia — Sydney' },
  { value: 'Pacific/Auckland', label: 'Pacific — Auckland' }
];

const NAIVE_ISO_PREFIX = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/;
const DATETIME_LOCAL_FILTER_RE =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,3}))?/;

export function getUserTimezone() {
  if (typeof localStorage === 'undefined') return '';
  return localStorage.getItem(TIMEZONE_STORAGE_KEY) || '';
}

export function setUserTimezone(value) {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(TIMEZONE_STORAGE_KEY, value || '');
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('commray-timezone-change'));
  }
}

function tzOpts() {
  const t = getUserTimezone();
  return t ? { timeZone: t } : {};
}

/** @param {string|undefined} ianaOverride undefined = read persisted preference; '' = browser local */
function tzOptsFor(ianaOverride) {
  if (ianaOverride === undefined) return tzOpts();
  return ianaOverride ? { timeZone: ianaOverride } : {};
}

/**
 * Parse API / log timestamps to a Date (UTC instant).
 * Naive ISO strings from Java LocalDateTime are treated as UTC.
 */
export function parseLogTimestampToDate(input) {
  if (input == null || input === '') return null;
  if (input instanceof Date) {
    return Number.isNaN(input.getTime()) ? null : input;
  }
  if (typeof input === 'number' && !Number.isNaN(input)) {
    return new Date(input);
  }
  if (Array.isArray(input) && input.length >= 3) {
    const [year, month, day, hour = 0, minute = 0, second = 0, nano = 0] = input;
    const ms = Math.floor(Number(nano) / 1_000_000) || 0;
    const d = new Date(Date.UTC(year, month - 1, day, hour, minute, second, ms));
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof input === 'object') {
    if (input.year != null && input.month != null && input.day != null) {
      const d = new Date(
        Date.UTC(
          input.year,
          (input.month || 1) - 1,
          input.day || 1,
          input.hour || 0,
          input.minute || 0,
          input.second || 0,
          input.nano ? Math.floor(input.nano / 1_000_000) : 0
        )
      );
      return Number.isNaN(d.getTime()) ? null : d;
    }
    const d = new Date(input);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof input === 'string') {
    const trimmed = input.trim();
    if (!trimmed) return null;
    if (NAIVE_ISO_PREFIX.test(trimmed) && !/[Zz]$|[+-]\d{2}:?\d{2}$/.test(trimmed)) {
      const normalized = trimmed.replace(' ', 'T');
      const d = new Date(normalized.endsWith('Z') ? normalized : `${normalized}Z`);
      return Number.isNaN(d.getTime()) ? null : d;
    }
    const d = new Date(trimmed);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

/** Epoch ms for chart bucketing / sorting. */
export function parseLogTimestampToMillis(input) {
  const d = parseLogTimestampToDate(input);
  return d ? d.getTime() : null;
}

/**
 * @returns {{ y, mo, da, h, mi, s, ms }} string parts from Intl, or null
 */
export function getDateTimePartsInUserTz(dateInput, ianaOverride) {
  const d =
    dateInput instanceof Date && !Number.isNaN(dateInput.getTime())
      ? dateInput
      : parseLogTimestampToDate(dateInput);
  if (!d || Number.isNaN(d.getTime())) return null;
  const base = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    ...tzOptsFor(ianaOverride)
  };
  const pick = (parts, type) => parts.find((p) => p.type === type)?.value ?? '';
  let ms = '000';
  try {
    const parts = new Intl.DateTimeFormat('en-GB', { ...base, fractionalSecondDigits: 3 }).formatToParts(d);
    const fs = parts.find((p) => p.type === 'fractionalSecond');
    if (fs && fs.value) ms = String(fs.value).replace(/\D/g, '').padEnd(3, '0').slice(0, 3);
    return {
      y: pick(parts, 'year'),
      mo: pick(parts, 'month'),
      da: pick(parts, 'day'),
      h: pick(parts, 'hour'),
      mi: pick(parts, 'minute'),
      s: pick(parts, 'second'),
      ms
    };
  } catch {
    const parts = new Intl.DateTimeFormat('en-GB', base).formatToParts(d);
    return {
      y: pick(parts, 'year'),
      mo: pick(parts, 'month'),
      da: pick(parts, 'day'),
      h: pick(parts, 'hour'),
      mi: pick(parts, 'minute'),
      s: pick(parts, 'second'),
      ms: '000'
    };
  }
}

/** Log table / CSV: YYYY-MM-DD HH:mm:ss.mmm in selected zone */
export function formatTimestampWithMillisUser(timestamp, ianaOverride) {
  const p = getDateTimePartsInUserTz(timestamp, ianaOverride);
  if (!p) return String(timestamp ?? '');
  const pad = (v, n) => String(v).padStart(n, '0');
  return `${p.y}-${pad(p.mo, 2)}-${pad(p.da, 2)} ${pad(p.h, 2)}:${pad(p.mi, 2)}:${pad(p.s, 2)}.${pad(p.ms, 3)}`;
}

/** Log Explorer timeline x-axis labels */
export function formatTimelineBucketLabelUser(date, intervalMs, ianaOverride) {
  const p = getDateTimePartsInUserTz(date, ianaOverride);
  if (!p) return '';
  const pad = (n) => String(n).padStart(2, '0');
  const { y, mo, da, h, mi, s, ms } = p;
  if (intervalMs < 1000) return `${pad(h)}:${pad(mi)}:${pad(s)}.${String(ms).padStart(3, '0')}`;
  if (intervalMs < 60 * 1000) return `${pad(h)}:${pad(mi)}:${pad(s)}`;
  if (intervalMs < 60 * 60 * 1000) return `${pad(mo)}/${pad(da)} ${pad(h)}:${pad(mi)}`;
  if (intervalMs < 24 * 60 * 60 * 1000) return `${pad(mo)}/${pad(da)} ${pad(h)}:00`;
  return `${y}-${pad(mo, 2)}-${pad(da, 2)}`;
}

/** Widget chart bucket labels (matches prior getFullYear-style layout, in user TZ) */
export function formatWidgetChartTimeKey(date, intervalMinutes, ianaOverride) {
  const p = getDateTimePartsInUserTz(date, ianaOverride);
  if (!p) return '';
  const pad2 = (x) => String(x).padStart(2, '0');
  const datePart = `${p.y}-${pad2(p.mo)}-${pad2(p.da)}`;
  if (intervalMinutes < 60) return `${datePart} ${pad2(p.h)}:${pad2(p.mi)}`;
  if (intervalMinutes < 1440) return `${datePart} ${pad2(p.h)}:00`;
  return datePart;
}

/**
 * Parse Start/End filter values from datetime-local inputs.
 * Wall-clock components are interpreted in the user's selected IANA zone (or browser local when unset).
 */
export function parseDatetimeLocalFilterToDate(localStr, ianaOverride) {
  if (localStr == null || localStr === '') return null;
  const trimmed = String(localStr).trim();
  if (!trimmed) return null;

  if (/[Zz]$|[+-]\d{2}:?\d{2}$/.test(trimmed)) {
    return parseLogTimestampToDate(trimmed);
  }

  const m = trimmed.match(DATETIME_LOCAL_FILTER_RE);
  if (!m) return null;

  const iana = ianaOverride === undefined ? getUserTimezone() : ianaOverride;

  if (iana === 'UTC') {
    return parseLogTimestampToDate(trimmed);
  }
  if (!iana) {
    const d = new Date(trimmed);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const [, y, mo, da, h, mi, s, frac = '0'] = m;
  const ms = String(frac).padEnd(3, '0').slice(0, 3);
  const targetUtc = Date.UTC(+y, +mo - 1, +da, +h, +mi, +s, +ms);

  let guess = new Date(targetUtc);
  for (let i = 0; i < 4; i++) {
    const p = getDateTimePartsInUserTz(guess, iana);
    if (!p) break;
    const shownUtc = Date.UTC(+p.y, +p.mo - 1, +p.da, +p.h, +p.mi, +p.s, +p.ms);
    const diff = targetUtc - shownUtc;
    if (diff === 0) return guess;
    guess = new Date(guess.getTime() + diff);
  }
  return guess;
}

export function parseDatetimeLocalFilterToMillis(localStr, ianaOverride) {
  const d = parseDatetimeLocalFilterToDate(localStr, ianaOverride);
  return d ? d.getTime() : null;
}

/** UTC naive ISO for backend LocalDateTime params from a filter datetime-local value. */
export function formatFilterTimeForApi(localStr, ianaOverride) {
  const d = parseDatetimeLocalFilterToDate(localStr, ianaOverride);
  if (!d) return String(localStr ?? '').trim();
  const p = getDateTimePartsInUserTz(d, 'UTC');
  if (!p) return String(localStr).trim();
  const pad = (v, n) => String(v).padStart(n, '0');
  return `${p.y}-${pad(p.mo, 2)}-${pad(p.da, 2)}T${pad(p.h, 2)}:${pad(p.mi, 2)}:${pad(p.s, 2)}.${pad(p.ms, 3)}`;
}

/** datetime-local input value (step 0.001) in selected zone from UTC log/API value */
export function formatDatetimeLocalForUser(raw, ianaOverride) {
  const p = getDateTimePartsInUserTz(raw, ianaOverride);
  if (!p) return '';
  const pad = (v, n) => String(v).padStart(n, '0');
  return `${p.y}-${pad(p.mo, 2)}-${pad(p.da, 2)}T${pad(p.h, 2)}:${pad(p.mi, 2)}:${pad(p.s, 2)}.${pad(p.ms, 3)}`;
}

export function formatDateTimeForUser(raw, ianaOverride) {
  if (raw == null || raw === '') return '—';
  const d = parseLogTimestampToDate(raw);
  if (!d) return String(raw);
  return d.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'medium', ...tzOptsFor(ianaOverride) });
}

export function formatDateOnlyForUser(raw, ianaOverride) {
  if (raw == null || raw === '') return '—';
  const d = parseLogTimestampToDate(raw);
  if (!d) return String(raw);
  return d.toLocaleDateString(undefined, { dateStyle: 'medium', ...tzOptsFor(ianaOverride) });
}

/** Short locale string for chart tooltips / timeLabel */
export function formatDateTimeCompactUser(dateInput, ianaOverride) {
  const d = parseLogTimestampToDate(dateInput);
  if (!d) return '';
  return d.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'medium', ...tzOptsFor(ianaOverride) });
}

/** Hour:minute in user zone (for compact chart axes) */
export function formatTimeShortUser(dateInput, ianaOverride) {
  const d = parseLogTimestampToDate(dateInput);
  if (!d) return '';
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', ...tzOptsFor(ianaOverride) });
}
